import React, { useState, useEffect } from 'react';
import { 
  Ticket, MessageCircle, Clock, AlertCircle, CheckCircle, 
  User, Mail, Calendar, Tag, Filter, Search, Plus,
  BookOpen, Send, Eye, Edit3, Archive
} from 'lucide-react';
import { api } from '../../services';

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    priority: 'all',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  
  // New message state
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState('response'); // 'response' or 'internal'
  const [isSolution, setIsSolution] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Knowledge base conversion state
  const [showKbModal, setShowKbModal] = useState(false);
  const [kbForm, setKbForm] = useState({
    question: '',
    answer: '',
    keywords: '',
    category: '',
    article_id: ''
  });

  const statusColors = {
    open: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    waiting_response: 'bg-orange-100 text-orange-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800'
  };

  const priorityColors = {
    low: 'bg-gray-100 text-gray-600',
    normal: 'bg-blue-100 text-blue-600',
    high: 'bg-orange-100 text-orange-600',
    urgent: 'bg-red-100 text-red-600'
  };

  const categoryIcons = {
    authentication: User,
    commissions: CheckCircle,
    tax_forms: BookOpen,
    email: Mail,
    technical: AlertCircle,
    reporting: Eye,
    website: Edit3,
    general: MessageCircle
  };

  useEffect(() => {
    fetchTickets();
  }, [filters, pagination.page]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });

      const response = await api.get(`/support-tickets?${params}`);
      
      if (response.data.success) {
        setTickets(response.data.data.tickets);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total,
          pages: response.data.data.pagination.pages
        }));
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await api.get(`/support-tickets/${ticketId}`);
      
      if (response.data.success) {
        setSelectedTicket(response.data.data.ticket);
        setTicketMessages(response.data.data.messages);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;

    try {
      setSendingMessage(true);
      
      const response = await api.post(`/support-tickets/${selectedTicket.id}/messages`, {
        message: newMessage,
        is_solution: isSolution,
        is_internal: messageType === 'internal'
      });

      if (response.data.success) {
        setTicketMessages(prev => [...prev, response.data.data]);
        setNewMessage('');
        setIsSolution(false);
        setMessageType('response');
        
        // Refresh ticket details to get updated status
        await fetchTicketDetails(selectedTicket.id);
        await fetchTickets(); // Refresh ticket list
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const openKbModal = (ticket) => {
    setKbForm({
      question: ticket.subject,
      answer: '',
      keywords: '',
      category: ticket.category,
      article_id: ''
    });
    setShowKbModal(true);
  };

  const addToKnowledgeBase = async () => {
    if (!selectedTicket || !kbForm.question || !kbForm.answer) return;

    try {
      const keywords = kbForm.keywords.split(',').map(k => k.trim()).filter(k => k);
      
      const response = await api.post(`/support-tickets/${selectedTicket.id}/add-to-kb`, {
        question: kbForm.question,
        answer: kbForm.answer,
        keywords,
        category: kbForm.category,
        article_id: kbForm.article_id || undefined
      });

      if (response.data.success) {
        setShowKbModal(false);
        setKbForm({ question: '', answer: '', keywords: '', category: '', article_id: '' });
        
        // Refresh ticket details
        await fetchTicketDetails(selectedTicket.id);
        await fetchTickets();
        
        alert('Successfully added to knowledge base!');
      }
    } catch (error) {
      console.error('Error adding to knowledge base:', error);
      alert('Error adding to knowledge base. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <Clock className="w-4 h-4" />;
      case 'in_progress': return <MessageCircle className="w-4 h-4" />;
      case 'waiting_response': return <AlertCircle className="w-4 h-4" />;
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      case 'closed': return <Archive className="w-4 h-4" />;
      default: return <Ticket className="w-4 h-4" />;
    }
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading support tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Support Tickets</h1>
          <p className="text-gray-600 mt-2">Manage and respond to agent support requests</p>
          <div className="mt-3 flex items-center space-x-2 text-sm">
            <Mail className="w-4 h-4 text-blue-600" />
            <span className="text-gray-600">Support Email:</span>
            <span className="font-medium text-blue-600">support@travelpluscrm.com</span>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">
            {pagination.total} total tickets
          </span>
        </div>
      </div>

      {/* Email System Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-blue-900">Email-to-Ticket System</h3>
            <p className="text-sm text-blue-700 mt-1">
              Agents can send support requests to <strong>support@travelpluscrm.com</strong> and tickets will be automatically created. 
              Replies from this system are sent from the same address.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="waiting_response">Waiting Response</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Categories</option>
              <option value="authentication">Authentication</option>
              <option value="commissions">Commissions</option>
              <option value="tax_forms">Tax Forms</option>
              <option value="email">Email</option>
              <option value="technical">Technical</option>
              <option value="reporting">Reporting</option>
              <option value="website">Website</option>
              <option value="general">General</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Priorities</option>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search tickets..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Tickets List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Tickets</h2>
          </div>
          <div className="p-6">
          
          {tickets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Ticket className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No tickets found matching your criteria.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => {
                const CategoryIcon = categoryIcons[ticket.category] || MessageCircle;
                
                return (
                  <div
                    key={ticket.id}
                    onClick={() => fetchTicketDetails(ticket.id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
                      selectedTicket?.id === ticket.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-mono text-gray-500">
                          {ticket.ticket_number}
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[ticket.status]}`}>
                          {getStatusIcon(ticket.status)}
                          <span className="ml-1 capitalize">{ticket.status.replace('_', ' ')}</span>
                        </span>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${priorityColors[ticket.priority]}`}>
                        {ticket.priority}
                      </span>
                    </div>
                    
                    <h3 className="font-medium text-gray-900 mb-1 line-clamp-2">
                      {ticket.subject}
                    </h3>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <CategoryIcon className="w-4 h-4" />
                          <span className="capitalize">{ticket.category.replace('_', ' ')}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <User className="w-4 h-4" />
                          <span>{ticket.agent_name || ticket.agent_email}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(ticket.created_at)}</span>
                      </div>
                    </div>

                    {ticket.message_count > 0 && (
                      <div className="mt-2 flex items-center space-x-1 text-xs text-gray-500">
                        <MessageCircle className="w-3 h-3" />
                        <span>{ticket.message_count} messages</span>
                        {ticket.last_message_at && (
                          <span>• Last: {formatDate(ticket.last_message_at)}</span>
                        )}
                      </div>
                    )}

                    {ticket.added_to_kb && (
                      <div className="mt-2 flex items-center space-x-1 text-xs text-green-600">
                        <BookOpen className="w-3 h-3" />
                        <span>Added to Knowledge Base</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
           )}
          </div>
        </div>

        {/* Ticket Details */}
        <div className="bg-white rounded-lg shadow">
          {selectedTicket ? (
            <>
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Ticket Details</h2>
                  {selectedTicket.status === 'resolved' && !selectedTicket.added_to_kb && (
                    <button
                      onClick={() => openKbModal(selectedTicket)}
                      className="flex items-center space-x-2 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                    >
                      <BookOpen className="w-4 h-4" />
                      <span>Add to KB</span>
                    </button>
                  )}
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {selectedTicket.subject}
                  </h3>
                  <span className="text-sm font-mono text-gray-500">
                    {selectedTicket.ticket_number}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[selectedTicket.status]}`}>
                      {getStatusIcon(selectedTicket.status)}
                      <span className="ml-1 capitalize">{selectedTicket.status.replace('_', ' ')}</span>
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Priority:</span>
                    <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${priorityColors[selectedTicket.priority]}`}>
                      {selectedTicket.priority}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Category:</span>
                    <span className="ml-2 capitalize">{selectedTicket.category.replace('_', ' ')}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Created:</span>
                    <span className="ml-2">{formatDate(selectedTicket.created_at)}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-medium text-gray-700 mb-2">From:</h4>
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span>{selectedTicket.agent_name || 'Unknown'}</span>
                    <Mail className="w-4 h-4 text-gray-400 ml-4" />
                    <span>{selectedTicket.agent_email}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-medium text-gray-700 mb-2">Description:</h4>
                  <div className="bg-gray-50 p-4 rounded-md">
                    <p className="whitespace-pre-wrap text-gray-700">{selectedTicket.description}</p>
                  </div>
                </div>

                {/* Messages */}
                {ticketMessages.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-700 mb-3">Messages ({ticketMessages.length})</h4>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {ticketMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`p-4 rounded-lg ${
                            message.sender_type === 'support' 
                              ? 'bg-blue-50 border-l-4 border-blue-500' 
                              : 'bg-gray-50 border-l-4 border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">
                                {message.sender_name || message.sender_email}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                message.sender_type === 'support' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {message.sender_type}
                              </span>
                              {message.is_solution && (
                                <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                                  Solution
                                </span>
                              )}
                              {message.is_internal && (
                                <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700">
                                  Internal
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500">
                              {formatDate(message.created_at)}
                            </span>
                          </div>
                          <p className="whitespace-pre-wrap text-gray-700">{message.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reply Form */}
                {selectedTicket.status !== 'closed' && (
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-700">Reply</h4>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <Mail className="w-3 h-3" />
                        <span>Sent from: support@travelpluscrm.com</span>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <div className="flex items-center space-x-4 mb-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="response"
                            checked={messageType === 'response'}
                            onChange={(e) => setMessageType(e.target.value)}
                            className="mr-2"
                          />
                          Response to Agent
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="internal"
                            checked={messageType === 'internal'}
                            onChange={(e) => setMessageType(e.target.value)}
                            className="mr-2"
                          />
                          Internal Note
                        </label>
                      </div>
                      
                      {messageType === 'response' && (
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={isSolution}
                            onChange={(e) => setIsSolution(e.target.checked)}
                            className="mr-2"
                          />
                          Mark as solution (resolves ticket)
                        </label>
                      )}
                    </div>

                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      rows={4}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                    
                    <div className="flex justify-end mt-3">
                      <button
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sendingMessage}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="w-4 h-4" />
                        <span>{sendingMessage ? 'Sending...' : 'Send Message'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="p-6">
              <div className="text-center py-12 text-gray-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Select a ticket to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Knowledge Base Modal */}
      {showKbModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Add to Knowledge Base
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question
                </label>
                <input
                  type="text"
                  value={kbForm.question}
                  onChange={(e) => setKbForm(prev => ({ ...prev, question: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Enter the question that this ticket answers..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Answer
                </label>
                <textarea
                  value={kbForm.answer}
                  onChange={(e) => setKbForm(prev => ({ ...prev, answer: e.target.value }))}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Provide a clear, helpful answer..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={kbForm.category}
                    onChange={(e) => setKbForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="authentication">Authentication</option>
                    <option value="commissions">Commissions</option>
                    <option value="tax_forms">Tax Forms</option>
                    <option value="email">Email</option>
                    <option value="technical">Technical</option>
                    <option value="reporting">Reporting</option>
                    <option value="website">Website</option>
                    <option value="general">General</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Article ID (optional)
                  </label>
                  <input
                    type="text"
                    value={kbForm.article_id}
                    onChange={(e) => setKbForm(prev => ({ ...prev, article_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="e.g., tax-5"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Keywords (comma-separated)
                </label>
                <input
                  type="text"
                  value={kbForm.keywords}
                  onChange={(e) => setKbForm(prev => ({ ...prev, keywords: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="e.g., w-9, w-8ben, tax form, country"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowKbModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addToKnowledgeBase}
                disabled={!kbForm.question || !kbForm.answer}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                Add to Knowledge Base
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;
